import { Injectable } from '@angular/core';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { ConfigService } from 'app/app-config.service';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Version of the cookie notice the stored decision belongs to. Bump it whenever
 * the purposes or the processors change: every user is then asked again instead
 * of being carried over on a consent they never gave for the new terms.
 *
 * A bump has to invalidate *both* stores that remember the answer, or the two
 * disagree and the banner never reopens — see `COOKIE_CONSENT_COOKIE_NAME`.
 */
export const COOKIE_CONSENT_VERSION = 2;

/**
 * Name of the cookie the banner library writes its own answer to.
 *
 * Version-suffixed on purpose. The library decides whether to show the banner
 * from this cookie alone (`hasAnswered()`), while `ConsentService` decides
 * whether analytics may run from the versioned localStorage record. Deriving the
 * name from the version keeps the two in step: bumping the version renames the
 * cookie, the old one stops being read, and the banner reopens for everybody.
 *
 * v1 was the un-suffixed `cookieconsent_status` written by the previous
 * dismiss-only notice — see `CookieConsentService`, which clears it.
 */
export const COOKIE_CONSENT_COOKIE_NAME = `cookieconsent_status_v${COOKIE_CONSENT_VERSION}`;

/**
 * What was decided, and when — the audit trail a supervisory authority asks for.
 */
export interface CookieConsentRecord {
    analytics: boolean;
    decidedAt: string;
}

/**
 * Single source of truth for whether analytics may run.
 *
 * Nothing non-essential is loaded until this reports `true`: the banner is an
 * opt-in, so the default for a user who has not answered is "no".
 *
 * The decision is not final: the "Cookies" link in the application footer opens a
 * preferences dialog that shows what is currently on, and `setAnalyticsConsent`
 * writes the new choice back.
 */
@Injectable({
    providedIn: 'root',
})
export class ConsentService {
    private readonly analyticsConsent: BehaviorSubject<boolean>;
    private activated = false;

    constructor(
        private storageService: StorageService,
        private cookieConsentService: NgcCookieConsentService,
        private configService: ConfigService,
    ) {
        this.analyticsConsent = new BehaviorSubject<boolean>(this.readRecord()?.analytics ?? false);
    }

    /**
     * Emits the current analytics permission, starting with the stored decision
     * (or `false` when there is none).
     */
    public get analyticsConsent$(): Observable<boolean> {
        return this.analyticsConsent.asObservable();
    }

    public get hasAnalyticsConsent(): boolean {
        return this.analyticsConsent.value;
    }

    /**
     * Starts listening to the banner. Called once from the root component; the
     * guard makes that a property of the service rather than of its caller.
     */
    public activate(): void {
        if (this.activated) {
            return;
        }
        this.activated = true;

        this.cookieConsentService.statusChange$.subscribe((event) => this.record(event.status === 'allow'));
    }

    /**
     * Records a decision taken outside the banner — the preferences dialog behind
     * the footer's "Cookies" link, which shows the current choice as a toggle
     * instead of asking again from scratch.
     */
    public setAnalyticsConsent(analytics: boolean): void {
        this.record(analytics);
        this.writeBannerCookie(analytics);
    }

    private record(analytics: boolean): void {
        this.storageService.setGlobalItem<CookieConsentRecord>(
            LocalStorageKeys.cookieConsent,
            { analytics, decidedAt: new Date().toISOString() },
            COOKIE_CONSENT_VERSION,
        );

        this.analyticsConsent.next(analytics);
    }

    /**
     * Mirrors the decision into the banner library's own cookie.
     *
     * That cookie is the only thing the library reads to decide whether to show
     * the notice (`hasAnswered()`), so a change saved in the dialog has to land
     * there too — otherwise the two stores drift and the banner can reappear
     * contradicting a choice the user just made. Written the way the library
     * writes it: same name, path and domain, one year out.
     */
    private writeBannerCookie(analytics: boolean): void {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);

        const parts = [
            `${COOKIE_CONSENT_COOKIE_NAME}=${analytics ? 'allow' : 'deny'}`,
            `expires=${expires.toUTCString()}`,
            'path=/',
        ];

        const domain = this.configService.getConfig.domain;
        if (domain) {
            parts.push(`domain=${domain}`);
        }

        document.cookie = parts.join(';');
    }

    private readRecord(): CookieConsentRecord | undefined {
        return this.storageService.getGlobalItem<CookieConsentRecord>(
            LocalStorageKeys.cookieConsent,
            COOKIE_CONSENT_VERSION,
        );
    }
}
